import React, { useState, useCallback, useEffect, useMemo } from "react";
import { format } from "date-fns";
import { useNavigate, useLocation } from "react-router-dom";
import { CheckIcon } from "lucide-react";
import { useSelector } from "react-redux";
import { headerStatusOptions, priorityMap, priorityOptions, reversePriorityMap, reverseStatusMap, statusMap, statusOptions } from "utils/types/table";
import useDebounce from "utils/hooks/debounce-hook";
import { formatUSDate } from "utils/date";
import { useNotesByReferral } from "utils/hooks/useNotesByReferral";
import { useCreateReferralNote } from "utils/hooks/useCreateReferralNote";
import { useReferralListSlice } from "../slice";
import { selectSocketNotification } from "app/slice";
import { NotificationItem, NotificationPagination } from "types/Notification";
import { Header } from 'app/components/common/Header.tsx';
import { AdminNotes } from "app/components/common/AdminNotes/AdminNotes";
import { Table, TableColumn } from "app/components/common/Table/Table";
import { DateFilterDropdown } from "app/components/common/Filters/DateFilterDropdown";
import ReferralDetailPanel from "./ReferralDetailPanel";

import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "app/components/ui/dropdown-menu";
import { useToast } from "app/components/ui/use-toast";
import NotificationsSocket from "app/pages/appointment-copy/components/notiSock";

const statusBadge = (status: string, isOpen: boolean) => {
  const variant = {
    Scheduled: { color: 'bg-[#DEE3FC] text-[#5B73F1]', label: 'Scheduled' },
    Contacted: { color: 'bg-[#FEF4D3] text-[#F7C525]', label: 'Contacted' },
    Declined: { color: 'bg-[#FFD9D9] text-[#B3171A]', label: 'Declined' },
    "Not contacted": { color: 'bg-[#F2F4F8] text-[#878D96]', label: 'Not contacted' },
  };
  // Convert API status to UI status if needed
  const displayStatus = reverseStatusMap[status] || status;
  const { color, label } = variant[displayStatus] || variant["Not contacted"];
  
  return (
    <span className={`inline-flex items-center justify-between w-[7.125rem] h-[1.4375rem] px-2 rounded-[0.3125rem] text-xs font-medium leading-[0.9rem] whitespace-nowrap ${color} group`}>
      {label}
      <img 
        src="images/chevron-down.svg" 
        alt="" 
        className={`ml-1 transition-opacity opacity-0 group-hover:opacity-100 group-focus:opacity-100 ${isOpen ? 'opacity-100' : 'group-data-[state=open]:hidden'}`}
      />
    </span>
  );
};

import { Referral, ReferralListResponse } from "../slice/types";

interface ReferralResponse {
  data: Referral[];
}

interface LocationState {
  selectedReferralId?: string;
  selectedAppointmentId?: string;
  fromNotification?: boolean;
}

// Utility function to deduplicate referrals by _id
const deduplicateReferrals = (referrals: Referral[]): Referral[] => {
  const seen = new Set<string>();
  return referrals.filter(referral => {
    if (seen.has(referral._id)) {
      return false;
    }
    seen.add(referral._id);
    return true;
  });
};

const ReferralListPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as LocationState;
  const { useGetReferralsQuery, useUpdateReferralMutation, useNotificationListQuery } = useReferralListSlice();
  const [searchQuery, setSearchQuery] = useState('');
  const [priorityFilter, setPriorityFilter] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
   // return time zone 
   const timeZone: string = Intl.DateTimeFormat().resolvedOptions().timeZone;
  
  // Notification pagination state
  const [notificationCurrentPage, setNotificationCurrentPage] = useState(1);
  const [notificationHasMore, setNotificationHasMore] = useState(true);
  const [isLoadingMoreNotifications, setIsLoadingMoreNotifications] = useState(false);
  const [allNotifications, setAllNotifications] = useState<NotificationItem[]>([]);
  
  const { data: notificationData, refetch: refetchNotifications } = useNotificationListQuery({
    page: notificationCurrentPage,
    limit: 20
  });
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const socketNotification = useSelector(selectSocketNotification);
  const [selected, setSelected] = useState<string | null>(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [allReferrals, setAllReferrals] = useState<Referral[]>([]);
  const [paginationInfo, setPaginationInfo] = useState({
    pageNumber: 1,
    pageSize: 20,
    totalDocuments: 0,
    totalPages: 1,
    hasNextPage: false
  });
  
  const [updateReferral] = useUpdateReferralMutation();
  const { notes, isLoading: isLoadingNotes, handleAddNote: handleAddNoteFromHook } = useNotesByReferral({ referralId: selected });
  const { createNote, isCreatingNote } = useCreateReferralNote({ referralId: selected });
  const [isFormOpen, setIsFormOpen] = useState(true);
  const [isNotesOpen, setIsNotesOpen] = useState(false);
  const { toast } = useToast();
  const [isDateFilterOpen, setIsDateFilterOpen] = useState(false);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState<string | null>(null);
  const [notificationSelectedId, setNotificationSelectedId] = useState<string | null>(null);

  // Memoize query params to prevent unnecessary re-renders
  const queryParams = useMemo(() => ({ 
    name: debouncedSearchQuery || undefined,
    status: statusFilter.length > 0 ? statusFilter : undefined,
    startDate: startDate ? format(startDate, 'yyyy-MM-dd') : undefined,
    endDate: endDate ? format(endDate, 'yyyy-MM-dd') : undefined,
    priority: priorityFilter.length > 0 ? priorityFilter : undefined,
    page: currentPage,
    limit: 20,
    timezone: timeZone
  }), [debouncedSearchQuery, statusFilter, startDate, endDate, priorityFilter, currentPage, timeZone]);

  const { data: referralsData, isLoading: isLoadingReferrals, isError, error, refetch } = useGetReferralsQuery(queryParams);

  // Create a stable filter key to detect filter changes
  const filterKey = useMemo(() => {
    return JSON.stringify({
      search: debouncedSearchQuery,
      status: statusFilter,
      startDate: startDate?.toISOString(),
      endDate: endDate?.toISOString(),
      priority: priorityFilter
    });
  }, [debouncedSearchQuery, statusFilter, startDate, endDate, priorityFilter]);

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
    setAllReferrals([]);
    setSelected(null);
  }, [filterKey]);

  // Reset notification pagination when component mounts
  useEffect(() => {
    setNotificationCurrentPage(1);
    setAllNotifications([]);
    setNotificationHasMore(true);
  }, []);

  // Handle referrals data and pagination
  useEffect(() => {
    if (referralsData?.data) {
      // The API response structure has data containing PaginationInfo
      const paginationData = referralsData.data as any;
      const documents = paginationData.documents;
      const pageNumber = paginationData.pageNumber;
      const pageSize = paginationData.pageSize;
      const totalDocuments = paginationData.totalDocuments;
      const totalPages = paginationData.totalPages;
      
      if (documents && Array.isArray(documents)) {
        // Remove duplicates from incoming data using utility function
        const uniqueDocuments = deduplicateReferrals(documents);
        
        // Log warning if duplicates were found in API response
        if (documents.length !== uniqueDocuments.length) {
          console.warn(`Removed ${documents.length - uniqueDocuments.length} duplicate referrals from API response`);
        }
        
        if (pageNumber === 1) {
          // First page - replace all data
          setAllReferrals(uniqueDocuments);
          
          // Select first row if no row is currently selected and we have data
          if (uniqueDocuments.length > 0 && !selected) {
            setSelected(uniqueDocuments[0]._id);
          }
        } else {
          // Subsequent pages - append data with deduplication
          setAllReferrals(prev => {
            const combined = [...prev, ...uniqueDocuments];
            return deduplicateReferrals(combined);
          });
        }
      }
      
      // Update pagination info
      setPaginationInfo({
        pageNumber: pageNumber || 1,
        pageSize: pageSize || 20,
        totalDocuments: totalDocuments || 0,
        totalPages: totalPages || 1,
        hasNextPage: pageNumber < totalPages
      });
    }
  }, [referralsData, selected]);

  // Additional safety: Ensure allReferrals is always deduplicated when data changes
  useEffect(() => {
    if (allReferrals.length > 0) {
      const uniqueReferrals = deduplicateReferrals(allReferrals);
      if (uniqueReferrals.length !== allReferrals.length) {
        console.warn(`Removed ${allReferrals.length - uniqueReferrals.length} duplicate referrals from existing data`);
        setAllReferrals(uniqueReferrals);
      }
    }
  }, [referralsData]); // Only run when new data comes from API

  // Load more function for infinite scroll
  const handleLoadMore = useCallback(() => {
    const canLoadMore = paginationInfo.hasNextPage && !isLoadingReferrals;
    
    if (canLoadMore) {
      setCurrentPage(prev => prev + 1);
    }
  }, [paginationInfo.hasNextPage, isLoadingReferrals]);

  // Load more function for notifications
  const handleLoadMoreNotifications = useCallback(() => {
    const canLoadMore = notificationHasMore && !isLoadingMoreNotifications;
    
    if (canLoadMore) {
      setIsLoadingMoreNotifications(true);
      setNotificationCurrentPage(prev => prev + 1);
    }
  }, [notificationHasMore, isLoadingMoreNotifications]);

  // Combined function to merge API and socket notifications with pagination
  const combineNotifications = () => {
    // Handle different response formats for notificationData
    let apiNotifications: NotificationItem[] = [];
    let notificationPagination: { pageNumber: number; pageSize: number; totalDocuments: number; totalPages: number } | null = null;
    
    if (notificationData) {
      if (Array.isArray(notificationData)) {
        apiNotifications = notificationData;
      } else if (typeof notificationData === 'object' && notificationData !== null) {
        const data = notificationData as any;
        // Handle new API structure: data.data.notifications.documents
        const notificationsData = data.data?.notifications?.documents || data.data?.notifications || data.notifications?.documents || data.notifications;
        apiNotifications = Array.isArray(notificationsData) ? notificationsData : [];
        
        // Extract pagination data
        const paginationData = data.data?.notifications || data.notifications;
        if (paginationData && typeof paginationData === 'object') {
          notificationPagination = {
            pageNumber: (paginationData as any).pageNumber || 1,
            pageSize: (paginationData as any).pageSize || 20,
            totalDocuments: (paginationData as any).totalDocuments || 0,
            totalPages: (paginationData as any).totalPages || 1
          };
        }
      }
    }
    
    const socketNotifications = socketNotification ? [socketNotification] : [];
    
    // Ensure both are arrays before spreading
    const socketArray = Array.isArray(socketNotifications) ? socketNotifications : [];
    
    // Handle pagination: append new notifications to existing ones
    if (notificationCurrentPage === 1) {
      // First page - replace all data
      // Filter socket notifications to only include those not present in API with isRead: true
      const filteredSocketArray = socketArray.filter(socketNotification => {
        const apiNotification = apiNotifications.find(api => 
          api._id === socketNotification._id || 
          (api.entityId && api.entityId === socketNotification.entityId)
        );
        
        // If no API notification exists, keep socket notification
        if (!apiNotification) {
          return true;
        }
        
        // If API notification exists and isRead is true, exclude socket notification
        // If API notification exists and isRead is false, keep socket notification (it's newer)
        return apiNotification.isRead !== true;
      });
      
      // Combine API notifications with filtered socket notifications
      const combinedNotifications = [...apiNotifications, ...filteredSocketArray];
      
      // Remove duplicates based on _id or entityId (API data will be first)
      const uniqueNotifications = combinedNotifications.filter((notification, index, self) => 
        index === self.findIndex(n => 
          n._id === notification._id || 
          (n.entityId && n.entityId === notification.entityId)
        )
      );
      
      setNotifications(uniqueNotifications);
      setAllNotifications(uniqueNotifications);
    } else {
      // Subsequent pages - append data
      // Filter socket notifications to only include those not present in API with isRead: true
      const filteredSocketArray = socketArray.filter(socketNotification => {
        const apiNotification = apiNotifications.find(api => 
          api._id === socketNotification._id || 
          (api.entityId && api.entityId === socketNotification.entityId)
        );
        
        // If no API notification exists, keep socket notification
        if (!apiNotification) {
          return true;
        }
        
        // If API notification exists and isRead is true, exclude socket notification
        // If API notification exists and isRead is false, keep socket notification (it's newer)
        return apiNotification.isRead !== true;
      });
      
      // Combine API notifications with existing notifications and filtered socket notifications
      const combinedNotifications = [...apiNotifications, ...allNotifications, ...filteredSocketArray];
      
      // Remove duplicates based on _id or entityId (API data will be first)
      const uniqueNotifications = combinedNotifications.filter((notification, index, self) => 
        index === self.findIndex(n => 
          n._id === notification._id || 
          (n.entityId && n.entityId === notification.entityId)
        )
      );
      
      setNotifications(uniqueNotifications);
      setAllNotifications(uniqueNotifications);
    }
    
    // Update pagination state
    if (notificationPagination) {
      const hasNextPage = notificationPagination.pageNumber < notificationPagination.totalPages;
      setNotificationHasMore(hasNextPage);
    }
    
    // Reset loading more state
    setIsLoadingMoreNotifications(false);
  };

  useEffect(() => {
    combineNotifications();
  }, [notificationData, socketNotification]);

  // Handle navigation state from appointment page
  useEffect(() => {
    if (locationState?.selectedReferralId && locationState?.fromNotification) {
      const referralId = locationState.selectedReferralId;
      
      // Wait for data to load, then select the referral
      if (allReferrals.length > 0) {
        const referral = allReferrals.find(row => row._id === referralId);
        if (referral) {
          setSelected(referral._id);
          setNotificationSelectedId(referral._id);
          
          // Scroll to the selected row
          const selectedRowElement = document.querySelector(`[data-row-id="${referral._id}"]`);
          if (selectedRowElement) {
            selectedRowElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
          
          toast({
            title: "Referral Selected",
            description: `Selected referral for ${referral.patientFirstName} ${referral.patientLastName}`,
          });
          
          // Clear the notification selection after a few seconds
          setTimeout(() => {
            setNotificationSelectedId(null);
          }, 3000);
          
          // Clear the navigation state
          navigate(location.pathname, { replace: true });
        }
      }
    }
  }, [allReferrals, locationState, navigate, location.pathname, toast]);

  // Add new handlers for panel state management
  const handleFormOpen = (open: boolean) => {
    setIsFormOpen(open);
    if (open) {
      setIsNotesOpen(false);
    }
  };

  const handleNotesOpen = (open: boolean) => {
    setIsNotesOpen(open);
    if (open) {
      setIsFormOpen(false);
    }
  };

  // Reset collapsible states when selection changes
  useEffect(() => {
    if (selected) {
      setIsFormOpen(true);
      setIsNotesOpen(false);
    }
  }, [selected]);

  // Handle API errors
  useEffect(() => {
    if (isError && error) {
      console.error('❌ Referrals API Error:', error);
      toast({
        title: "Error Loading Referrals",
        description: error[0] || "Failed to load referrals. Please try again.",
        variant: "destructive",
      });
    }
  }, [isError, error, toast]);

  const handleSearch = (searchTerm: string) => {
    setSearchQuery(searchTerm);
  };

  const handleRowClick = (row: Referral) => {
    setSelected(row._id);
    // Clear notification selection when user manually selects a row
    setNotificationSelectedId(null);
  };

  const handleStatusUpdate = async (referralId: string, newStatus: string) => {
    try {
      // Update local state immediately for better UX
      setAllReferrals((prev) =>
        prev.map((row) =>
          row._id === referralId ? { ...row, status: newStatus } : row
        )
      );
      
      await updateReferral({
        referralId,
        data: { status: newStatus }
      }).unwrap();
      
      toast({
        title: "Status updated successfully",
        description: "The referral status has been updated.",
      });
      
      setStatusDropdownOpen(null);
    } catch (error: any) {
      console.error('Failed to update referral status:', error);
      toast({
        title: "Failed to update status",
        description: error?.data?.message || error?.message || "There was an error updating the referral status.",
        variant: "destructive",
      });
    }
  };

  const handlePriorityToggle = (priority: string) => {
    if (priority === "All") {
      setPriorityFilter([]);
    } else {
      setPriorityFilter(prev => {
        const priorityValue = priorityMap[priority];
        if (prev.includes(priorityValue)) {
          return prev.filter(p => p !== priorityValue);
        } else {
          return [...prev, priorityValue];
        }
      });
    }
  };

  const handleStatusToggle = (status: string) => {
    if (status === "All") {
      setStatusFilter([]);
    } else {
      setStatusFilter(prev => {
        if (prev.includes(statusMap[status])) {
          return prev.filter(s => s !== statusMap[status]);
        } else {
          return [...prev, statusMap[status]];
        }
      });
    }
  };

  const handleAddNote = async (content: string) => {
    const success = await createNote(content);
    if (success) {
      toast({
        title: "Note added successfully",
        description: "Note added successfully",
      });
    } else {
      toast({
        title: "Note added failed",
        description: "Note added failed",
      });
    }
  };

  const handleNotificationSelect = (entityId: string | null) => {
    if (entityId) {
      // Find the referral with matching ID in the current data
      const referral = allReferrals.find(row => row._id === entityId);
      
      if (referral) {
        // Select the referral
        setSelected(referral._id);
        setNotificationSelectedId(referral._id);
        
        // Scroll to the selected row if needed
        const selectedRowElement = document.querySelector(`[data-row-id="${referral._id}"]`);
        if (selectedRowElement) {
          selectedRowElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        
        // Clear the notification selection after a few seconds
        setTimeout(() => {
          setNotificationSelectedId(null);
        }, 3000);
      } else {
        // Check if this notification is for an appointment instead
        const notification = notifications.find(n => n.entityId === entityId);
        
        if (notification?.notificationType === 'Appointment' || notification?.type === 'appointment') {
          // Navigate to appointment page with the entityId
          navigate('/appointment-requests', { 
            state: { 
              selectedAppointmentId: entityId,
              fromNotification: true 
            } 
          });
        } else {
          // If referral not found in current data, it might be filtered out
          toast({
            title: "Referral Not Found",
            description: "The selected referral is not visible in the current view. Clearing filters to show all referrals.",
            variant: "destructive",
          });
          
          // Clear filters to show all referrals
          setPriorityFilter([]);
          setStatusFilter([]);
          setSearchQuery('');
          setStartDate(null);
          setEndDate(null);
        }
      }
    }
  };

  // Function to refetch notifications after marking as read
  const handleNotificationRead = useCallback(() => {
    // Refetch notifications to get updated read/unread counts
    refetchNotifications();
  }, [refetchNotifications]);

  const columns: TableColumn<Referral>[] = [
    {
      key: "createdAt", 
      header: () => (
        <DateFilterDropdown
          startDate={startDate}
          endDate={endDate}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
        />
      ),
      width: '4.1875rem',
      render: (row) => (
        <div className="text-[#21272A] text-sm font-normal leading-normal whitespace-nowrap">
          {row.createdAt ? formatUSDate(row.createdAt) : '-'}
        </div>
      )
    },
    { 
      key: "referringDoctorName", 
      header: () => (
        <div className="flex items-center">
          <h3 className="text-[#343A3F] text-xs font-normal leading-[0.9rem] tracking-[-0.0075rem] whitespace-nowrap">
            REFERRING Dr.
          </h3>
        </div>
      ),
      width: '5.75rem',
      render: (row) => (
        <div className="text-[#21272A] text-sm font-normal leading-normal pl-0.5">
          {row.referringDoctorName}
        </div>
      )
    },
    { 
      key: "practiceName",
      header: () => (
        <div className="flex items-center">
          <h3 className="uppercase text-[#343A3F] text-xs font-normal leading-[0.9rem] tracking-[-0.0075rem]">
            PRACTICE NAME
          </h3>
        </div>
      ),
      width: '9.5rem',
      // responsiveWidth: '6.5rem',
      render: (row) => (
        <div className="text-[#21272A] text-sm font-normal leading-normal pl-0.5">
          {row.practiceName}
        </div>
      )
    },
    { 
      key: "patientFirstName", 
      header: () => (
        <div className="flex items-center">
          <h3 className="text-[#343A3F] text-xs font-normal leading-[0.9rem] tracking-[-0.0075rem] whitespace-nowrap">
            Pt. FIRST NAME
          </h3>
        </div>
      ),
      width: '6.375rem',
      // responsiveWidth: '5.5rem',
      render: (row) => (
        <div className="text-[#21272A] text-sm font-normal leading-normal pl-3">
          {row.patientFirstName}
        </div>
      )
    },
    { 
      key: "patientLastName", 
      header: () => (
        <div className="flex items-center">
          <h3 className="text-[#343A3F] text-xs font-normal leading-[0.9rem] tracking-[-0.0075rem] whitespace-nowrap">
            Pt. LAST NAME
          </h3>
        </div>
      ),
      width: '6.375rem',
      // responsiveWidth: '4.75rem',
      render: (row) => (
        <div className="text-[#21272A] text-sm font-normal leading-normal pl-3">
          {row.patientLastName}
        </div>
      )
    },
    // {
    //   key: 'priority',
    //   header: () => (
    //     <div className="flex items-center">
    //       <DropdownMenu>
    //         <DropdownMenuTrigger asChild>
    //           <div className={`flex justify-center items-center hover:bg-[#F2F4F8] w-[5.3125rem] py-1 rounded cursor-pointer ${priorityFilter.length > 0 ? 'bg-[#F2F4F8] gap-0.5' : 'gap-1'} data-[state=open]:bg-[#F2F4F8]`}>
    //             {priorityFilter.length > 0 && (
    //               <svg xmlns="http://www.w3.org/2000/svg" width="7" height="6" viewBox="0 0 7 6" fill="none">
    //                 <circle cx="3.5" cy="3" r="3" fill="#55B494"/>
    //               </svg>
    //             )}
    //             <h3 className="uppercase text-[#343A3F] text-xs font-normal leading-[0.9rem] tracking-[-0.0075rem]">
    //               Priority
    //             </h3>
    //             <img src="images/chevron-down.svg" alt="" className="mt-px" />
    //           </div>
    //         </DropdownMenuTrigger>
    //         <DropdownMenuContent
    //           className="min-w-[5.3125rem] max-w-[5.3125rem] bg-white p-0 border-none rounded shadow-[0px_1px_4px_0px_rgba(0,0,0,0.15)]"
    //           sideOffset={2}
    //         >
    //           {priorityOptions.map((option) => (
    //             <DropdownMenuItem
    //               key={option}
    //               className={`flex gap-1.5 items-center group px-2 py-1.5 cursor-pointer ${(option === "All" && priorityFilter.length === 0) || (option !== "All" && priorityFilter.includes(priorityMap[option])) ? 'focus:bg-white' : 'focus:bg-[#DDE1E6]'}`}
    //               onSelect={() => handlePriorityToggle(option)}
    //             >
    //               <div className={`flex-shrink-0 flex items-center justify-center size-[0.5625rem] border border-[#343A3F] rounded-[0.0625rem] ${option === "All" ? (priorityFilter.length === 0 ? 'bg-[#5B73F1]' : '') : (priorityFilter.includes(priorityMap[option]) ? 'bg-[#5B73F1]' : '')}`}>
    //                 {(option === "All" && priorityFilter.length === 0) || (option !== "All" && priorityFilter.includes(priorityMap[option])) ? (
    //                   <CheckIcon className="w-3 h-3 text-white" />
    //                 ) : null}
    //               </div>
    //               <span className="text-black text-[0.625rem] font-normal leading-3 tracking-[-0.00625rem]">
    //                 {option}
    //               </span>
    //             </DropdownMenuItem>
    //           ))}
    //         </DropdownMenuContent>
    //       </DropdownMenu>
    //     </div>
    //   ),
    //   width: '5.3125rem',
    //   render: (row) => (
    //     <div className={`text-[#21272A] text-sm font-normal leading-normal ${row.priority ? 'pl-6' : 'pl-11'}`}>
    //       {row.priority ? reversePriorityMap[row.priority] || row.priority : '-'}
    //     </div>
    //   ),
    // },
    {
      key: "status",
      header: () => (
        <div className="flex items-center">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <div className={`flex justify-center items-center gap-2 hover:bg-[#F2F4F8] w-[7.125rem] py-1 rounded cursor-pointer ${statusFilter.length > 0 ? 'bg-[#F2F4F8]' : ''} data-[state=open]:bg-[#F2F4F8]`}>
                {statusFilter.length > 0 && (
                  <svg xmlns="http://www.w3.org/2000/svg" width="7" height="6" viewBox="0 0 7 6" fill="none">
                    <circle cx="3.5" cy="3" r="3" fill="#55B494"/>
                  </svg>
                )}
                <h3 className="uppercase text-[#343A3F] text-xs font-normal leading-[0.9rem] tracking-[-0.0075rem]">
                  Status
                </h3>
                <img src="images/chevron-down.svg" alt="" className="mt-px" />
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="min-w-[7.125rem] max-w-[7.125rem] bg-white p-0 border-none rounded shadow-[0px_1px_4px_0px_rgba(0,0,0,0.15)]"
              sideOffset={2}
            >
              {headerStatusOptions.map((option) => (
                <DropdownMenuItem
                  key={option}
                  className={`flex gap-1.5 items-center group px-2 py-1.5 cursor-pointer ${(option === "All" && statusFilter.length === 0) || (option !== "All" && statusFilter.includes(statusMap[option])) ? 'focus:bg-white' : 'focus:bg-[#DDE1E6]'}`}
                  onSelect={() => handleStatusToggle(option)}
                >
                  <div className={`flex-shrink-0 flex items-center justify-center size-[0.5625rem] border border-[#343A3F] rounded-[0.0625rem] ${option === "All" ? (statusFilter.length === 0 ? 'bg-[#5B73F1]' : '') : (statusFilter.includes(statusMap[option]) ? 'bg-[#5B73F1]' : '')}`}>
                    {(option === "All" && statusFilter.length === 0) || (option !== "All" && statusFilter.includes(statusMap[option])) ? (
                      <CheckIcon className="w-3 h-3 text-white" />
                    ) : null}
                  </div>
                  <span className="text-black text-[0.625rem] font-normal leading-3 tracking-[-0.00625rem]">
                    {option}
                  </span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
      width: '7.125rem',
      render: (row) => {
        const status = row.status;
        return (
          <div className="">
            <DropdownMenu open={statusDropdownOpen === row._id} onOpenChange={(open) => setStatusDropdownOpen(open ? row._id : null)}>
              <DropdownMenuTrigger asChild>
                <button 
                  type="button" 
                  className="pl-1 focus:outline-none"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  {statusBadge(status, statusDropdownOpen === row._id)}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="min-w-[7.125rem] max-w-[7.125rem] bg-white p-0 border-none rounded shadow-[0px_1px_4px_0px_rgba(0,0,0,0.15)]"
                sideOffset={2}
              >
                {statusOptions.map((option) => (
                  <DropdownMenuItem
                    key={option}
                    className={`flex gap-1.5 items-center group px-2 py-1.5 cursor-pointer ${reverseStatusMap[status] === option ? 'focus:bg-white' : 'focus:bg-[#DDE1E6]'}`}
                    onSelect={() => handleStatusUpdate(row._id, statusMap[option])}
                  >
                    <div className={`flex-shrink-0 flex items-center justify-center size-[0.5625rem] border border-[#343A3F] rounded-[0.0625rem] ${reverseStatusMap[status] === option ? 'bg-[#5B73F1]' : ''}`}>
                      {reverseStatusMap[status] === option && (
                        <CheckIcon className="w-3 h-3 text-white" />
                      )}
                    </div>
                    <span className="text-black text-[0.625rem] font-normal leading-3 tracking-[-0.00625rem]">
                      {option}
                    </span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
    },
  ];



  return (
    <React.Fragment>
      <Header 
        onSearch={handleSearch} 
        notifications={notifications} 
        onNotificationSelect={handleNotificationSelect}
        onNotificationRead={handleNotificationRead}
        notificationPagination={{
          pageNumber: notificationCurrentPage,
          pageSize: 20,
          totalDocuments: 0, // This will be updated from API response
          totalPages: 1
        }}
        onLoadMoreNotifications={handleLoadMoreNotifications}
        notificationHasMore={notificationHasMore}
        isLoadingMoreNotifications={isLoadingMoreNotifications}
        // paddingBottom="pb-16"
      />
      <NotificationsSocket onReferralNotification={() => {
        refetch();
        toast({
          title: "Referrals Updated",
          description: "Referral list has been refreshed with latest data.",
        });
      }} />
      <div className="flex-1 pt-[6.5rem]">
        <div className="h-[calc(100vh-5rem)] p-6 overflow-y-auto custom-scrollbar">
          <div className="flex gap-6 bg-[#F2F4F8] overflow-x-auto [&::-webkit-scrollbar]:hidden">
            {/* Left: Table */}
            <div className="flex-grow flex-shrink-0 min-w-[44.625rem]">
              <Table<Referral>
                columns={columns}
                data={allReferrals}
                isLoading={isLoadingReferrals}
                hasMore={paginationInfo.hasNextPage}
                onLoadMore={handleLoadMore}
                rowKey={(row) => row._id}
                emptyMessage="No referrals found."
                onRowClick={handleRowClick}
                selectedId={selected}
                selectedStyles={notificationSelectedId === selected ? 'bg-[#DDE1E680] hover:bg-[#DDE1E690] border-l-2 border-l-[#55B494]' : 'bg-[#DDE1E680] hover:bg-[#DDE1E690] border-l-2'}
                headerPadding="px-1"
                firstHeaderPadding = 'pl-3.5'
                lastHeaderPadding = 'pr-2.5'
                tableHeight="h-[calc(100vh-11.5375rem)]"
              />
            </div>
            {/* Right: Details Panel */}
            <div className="flex-shrink-0 w-[clamp(23.75rem,calc(100vw-72.225rem),40rem)] flex flex-col gap-6">
              <ReferralDetailPanel
                isFormOpen={isFormOpen}
                isNotesOpen={isNotesOpen}
                setIsFormOpen={handleFormOpen}
                referralId={selected || ''} 
              />
              <AdminNotes
                isFormOpen={isFormOpen}
                isNotesOpen={isNotesOpen}
                setIsNotesOpen={handleNotesOpen}
                notes={notes?.data}
                isLoading={isLoadingNotes}
                onAddNote={handleAddNote}
              />
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default ReferralListPage;
